import { Link } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Database, Server, GitBranch, Lock, FileCode, Zap, CheckCircle } from "lucide-react"

export function HomePage() {
  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4 py-8">
        <h1 className="text-4xl font-bold tracking-tight">
          NestJS Production-Ready Templates
        </h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Kickstart your next project with battle-tested Node.js (Frontend/Backend) framework templates featuring authentication, 
          database integrations, and best practices out of the box.
        </p>
        <div className="flex gap-4 justify-center pt-4">
          <Button asChild size="lg">
            <Link to="/nestjs/mongodb">
              <Database className="mr-2 h-5 w-5" />
              MongoDB Template
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/nestjs/mysql">
              <Database className="mr-2 h-5 w-5" />
              MySQL Template
            </Link>
          </Button>
        </div>
      </div>

      {/* Key Features */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Key Features</CardTitle>
          <CardDescription>
            Everything you need to build production-grade applications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Lock className="h-6 w-6 text-primary" />}
              title="Complete Authentication"
              description="JWT-based auth with refresh tokens, OAuth providers (Google, GitHub, Discord), email verification, and password reset."
            />
            <FeatureCard
              icon={<Database className="h-6 w-6 text-primary" />}
              title="Database Ready"
              description="Pre-configured with MongoDB/Mongoose or MySQL/TypeORM. Includes migrations, relationships, and best practices."
            />
            <FeatureCard
              icon={<FileCode className="h-6 w-6 text-primary" />}
              title="TypeScript Strict Mode"
              description="Fully typed codebase with strict TypeScript configuration for maximum type safety."
            />
            <FeatureCard
              icon={<Server className="h-6 w-6 text-primary" />}
              title="NestJS Best Practices"
              description="Follows official NestJS patterns with modular architecture, dependency injection, and guards."
            />
            <FeatureCard
              icon={<GitBranch className="h-6 w-6 text-primary" />}
              title="API Documentation"
              description="Swagger/OpenAPI documentation auto-generated for all endpoints with proper decorators."
            />
            <FeatureCard
              icon={<Zap className="h-6 w-6 text-primary" />}
              title="Developer Experience"
              description="Hot reload, ESLint configuration, environment validation, and helper scripts included."
            />
          </div>
        </CardContent>
      </Card>

      {/* Templates Overview */}
      <div className="grid md:grid-cols-2 gap-6">
        <TemplateCard
          title="MongoDB Template"
          description="Perfect for flexible, document-based data modeling"
          features={[
            "Mongoose ODM",
            "Schema-based modeling",
            "Flexible data structure",
            "Great for rapid prototyping",
          ]}
          link="/nestjs/mongodb"
          githubLink="https://github.com/ALIRAZA47/template-nestjs-mongodb"
        />
        <TemplateCard
          title="MySQL Template"
          description="Ideal for relational data with strict schemas"
          features={[
            "TypeORM integration",
            "Migration system",
            "Strong relational support",
            "Production-grade reliability",
          ]}
          link="/nestjs/mysql"
          githubLink="https://github.com/ALIRAZA47/template-nestjs-mysql"
        />
      </div>

      {/* Getting Started */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Quick Start</CardTitle>
          <CardDescription>
            Get up and running in minutes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <QuickStartStep
              number={1}
              title="Clone the Repository"
              description="Fork or clone the template you want to use"
            />
            <QuickStartStep
              number={2}
              title="Install Dependencies"
              description="Run the setup script to install packages and configure environment"
            />
            <QuickStartStep
              number={3}
              title="Configure Environment"
              description="Copy .env.sample to .env and fill in your configuration"
            />
            <QuickStartStep
              number={4}
              title="Start Development"
              description="Run npm run start:dev and start building!"
            />
          </div>
        </CardContent>
      </Card>

      {/* Why Choose These Templates */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Why Choose These Templates?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                Production Ready
              </h3>
              <p className="text-muted-foreground">
                Not just boilerplate - these templates include real-world patterns, 
                error handling, logging, and security best practices used in production applications.
              </p>
            </div>
            <div className="space-y-3">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                Well Documented
              </h3>
              <p className="text-muted-foreground">
                Every module, service, and configuration is thoroughly documented. 
                Understand not just what code does, but why it's structured that way.
              </p>
            </div>
            <div className="space-y-3">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                Modular Architecture
              </h3>
              <p className="text-muted-foreground">
                Clean separation of concerns with feature modules, shared utilities, 
                and reusable components. Easy to extend and maintain.
              </p>
            </div>
            <div className="space-y-3">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                Active Maintenance
              </h3>
              <p className="text-muted-foreground">
                Regularly updated with latest NestJS versions, security patches, 
                and community feedback incorporated.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function FeatureCard({ 
  icon, 
  title, 
  description 
}: { 
  icon: React.ReactNode
  title: string
  description: string 
}) {
  return (
    <div className="flex gap-4">
      <div className="shrink-0">{icon}</div>
      <div className="space-y-1">
        <h3 className="font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}

function TemplateCard({
  title,
  description,
  features,
  link,
  githubLink,
}: {
  title: string
  description: string
  features: string[]
  link: string
  githubLink: string
}) {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-xl">{title}</CardTitle>
            <CardDescription className="mt-2">{description}</CardDescription>
          </div>
          <Badge variant="secondary">NestJS</Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-4">
        <ul className="space-y-2 flex-1">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-primary shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <Button asChild className="flex-1">
            <Link to={link}>View Docs</Link>
          </Button>
          <Button asChild variant="outline">
            <a href={githubLink} target="_blank" rel="noopener noreferrer">
              <GitBranch className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function QuickStartStep({
  number,
  title,
  description,
}: {
  number: number
  title: string
  description: string
}) {
  return (
    <div className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">
        {number}
      </div>
      <div className="space-y-1 flex-1">
        <h4 className="font-semibold">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}

